/* eslint-disable react/jsx-no-bind */
import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { Add, EditOutlined } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertsContext } from '../shared/alerts/Alerts.context';
import LoggingService from '../shared/Logging.api';
import SpinningSpark from '../shared/components/SpinningSpark';
import colors from '../colors.styles';
import adminFormStyles from '../shared/styles/adminForm.styles';
import TasksApi from './Tasks.api';
import ContentRequestBar from './ContentRequestBar';
import DraftsModal from './DraftsModal';
import {
  DEFAULT_LOOKBACK,
  LOOKBACK_OPTIONS,
  getTasksForDisplay,
} from './Tasks.service';

const styles = {
  page: adminFormStyles.page,
  content: {
    ...adminFormStyles.content,
    maxWidth: 1100,
  },
  header: {
    alignItems: { xs: 'stretch', sm: 'center' },
    flexDirection: { xs: 'column', sm: 'row' },
    gap: 2,
    justifyContent: 'space-between',
    mb: 3,
  },
  title: adminFormStyles.pageTitle,
  actions: {
    flexDirection: { xs: 'column', sm: 'row' },
    gap: 1.5,
  },
  draftsButton: {
    ...adminFormStyles.secondaryButton,
    color: colors.base.bentonville_blue,
  },
  createButton: adminFormStyles.primaryButton,
  lookback: {
    alignItems: 'center',
    display: 'flex',
    gap: 1.5,
    justifyContent: 'flex-end',
    mb: 2,
  },
  lookbackLabel: {
    color: '#656d80',
    fontSize: '14px',
  },
  lookbackSelect: {
    backgroundColor: colors.base.light,
    borderRadius: '8px',
    fontSize: '14px',
    minWidth: 140,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#dbe2e6' },
  },
  empty: {
    color: '#656d80',
    fontSize: '14px',
    py: 6,
    textAlign: 'center',
  },
  loading: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    minHeight: 320,
  },
};

export default function TasksPage() {
  const navigate = useNavigate();
  const { showAlert } = useContext(AlertsContext);
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lookback, setLookback] = useState(DEFAULT_LOOKBACK);
  // Drafts lives in the URL so browser back from an edited draft reopens it.
  const [searchParams, setSearchParams] = useSearchParams();
  const draftsOpen = searchParams.get('drafts') === 'open';

  function openDrafts() {
    setSearchParams({ drafts: 'open' });
  }

  function closeDrafts() {
    setSearchParams({}, { replace: true });
  }

  useEffect(() => {
    loadTasks();
  }, []);

  async function loadTasks() {
    setIsLoading(true);
    try {
      const fetchedTasks = await TasksApi.getTasks();
      setTasks(fetchedTasks);
    } catch (error) {
      LoggingService.error('Failed to load content requests', error);
      showAlert('Failed to load content requests. Please try again.', 'error');
      setTasks([]);
    } finally {
      setIsLoading(false);
    }
  }

  const { listedTasks, drafts } = getTasksForDisplay(tasks, lookback);

  return (
    <Box sx={styles.page}>
      <Box sx={styles.content}>
        <Stack sx={styles.header}>
          <Typography component="h1" sx={styles.title}>
            Content Requests
          </Typography>
          <Stack sx={styles.actions}>
            <Button
              onClick={openDrafts}
              startIcon={<EditOutlined />}
              sx={styles.draftsButton}
              variant="outlined"
            >
              Drafts ({drafts.length})
            </Button>
            <Button
              onClick={() => navigate('/tasks/new')}
              startIcon={<Add />}
              sx={styles.createButton}
              variant="contained"
            >
              Create new request
            </Button>
          </Stack>
        </Stack>

        {isLoading ? (
          <Box sx={styles.loading}>
            <SpinningSpark />
          </Box>
        ) : (
          <>
            <Box sx={styles.lookback}>
              <Typography sx={styles.lookbackLabel}>
                Show completed from
              </Typography>
              <Select
                aria-label="Show completed from"
                onChange={(event) => setLookback(event.target.value)}
                size="small"
                sx={styles.lookbackSelect}
                value={lookback}
              >
                {LOOKBACK_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            {listedTasks.length === 0 ? (
              <Typography sx={styles.empty}>
                No content requests yet.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {listedTasks.map((task) => (
                  <ContentRequestBar key={task.task_id} task={task} />
                ))}
              </Stack>
            )}
          </>
        )}
      </Box>
      <DraftsModal drafts={drafts} isOpen={draftsOpen} onClose={closeDrafts} />
    </Box>
  );
}
